<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useAuthUser } from '@compositions/Auth';
/***/
import { useApplicationStore } from '@stores/application';
import { useAuthCustomerPlanningStatusStore } from '@stores/auth/planning/customerPlanningStatus';
import { useHistoryStore } from '@stores/history';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const Wrapper = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/List/Customer/Wrapper'));
const FooterList = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/List/Customer/List/FooterList'));
const FirstTimeModal = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/List/Customer/List/FirstTimeModal'));

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Stores
const applicationStore = useApplicationStore();
const customerPlanningStatusStore = useAuthCustomerPlanningStatusStore();
const historyStore = useHistoryStore();

// Vrai s'il s'agit de la première visite après la validation des données
const canDisplayFirstTimeModal = ref(route.query['first-time'] === '1');

// Retourne l'application courante
const currentApplication = computed(() => applicationStore.getCurrent);

// Composition pour l'utilisateur connecté
const { authUser, authUserHasPermission } = useAuthUser({});

// Vrai si l'utilisateur peut créer des tâches
const canCreateTask = computed(() => authUserHasPermission('create task'));

// Vrai si l'utilisateur est autorisée à accéder à la page
const allowed = ref(false);

// Mode d'affichage des tâches
const currentDisplayMode = ref('month');

// Mois actuellement affiché
const currentMonth = ref(null);

// Vrai si un footer est affiché en bas de page
const withFooterBottom = computed(() => (canCreateTask.value || canGenerateNewTasks.value));

// Vrai si l'utilisateur peut générer de nouvelles idées
const canGenerateNewTasks = computed(() => (currentMonthPlanningStatus.value?.canGenerateNewTasks ?? false));

// Classes à utiliser pour div.layout
const layoutClasses = computed(() => {
    const classes = [
        'wrapper_content',
        'wrapper-content-task-list'
    ];

    if(withFooterBottom.value) {
        classes.push('with-footer-bottom');
    }

    return classes;
});

// Statut du planning actuellement affiché
const currentMonthPlanningStatus = computed(() => {
    if(authUser.value === null || currentMonth.value === null || currentDisplayMode.value !== 'month') {
        return null;
    }

    return customerPlanningStatusStore.getPlanningStatusByPeriod(
        authUser.value,
        currentMonth.value.year,
        currentMonth.value.month
    );
});

// Liste des tâches
const taskList = ref([]);

onMounted(() => {
    const canConsult = (authUser.value.type === 'CUSTOMER' && authUserHasPermission('consult tasks'));
    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        allowed.value = true;
        document.title = t('page.me.task.list.customer.title');
    }
});
</script>

<template>
    <layout
        v-if="allowed"
        :wrapper-content-classes="layoutClasses.join(' ')"
    >
        <wrapper
            :current-display-mode="currentDisplayMode"
            :task-list="taskList"
            v-on:update:task-list="taskList = $event"
            v-on:update:current-display-mode="currentDisplayMode = $event"
            v-on:change-month="currentMonth = $event"
        ></wrapper>

        <footer-list
            v-if="currentMonthPlanningStatus !== null && (canCreateTask || canGenerateNewTasks)"
            :planning-status="currentMonthPlanningStatus"
            :tasks="taskList"
        ></footer-list>

        <first-time-modal
            v-if="canDisplayFirstTimeModal && currentMonthPlanningStatus !== null"
            :planning-status="currentMonthPlanningStatus"
            @close="canDisplayFirstTimeModal = false"
        ></first-time-modal>
    </layout>
</template>
